import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import InsufficientPermisionPage from 'src/pages/InsufficientPermissionPage';
import useAuth from '../hooks/useAuth';

interface PermissionGuardProps {
  /** Every listed permission is required. Mutually exclusive with `anyOf`. */
  required?: string | string[];
  /**
   * At least ONE of these permissions is required. For a page whose tools are gated individually
   * inside it (e.g. Device Operations: provisioning vs bulk provisioning), where holding either
   * permission is reason enough to open the page.
   */
  anyOf?: string[];
  children: ReactNode;
  fallbackRoute?: string;
}

const PermissionGuard: FC<PermissionGuardProps> = (props) => {
  const { required, anyOf, children, fallbackRoute } = props;
  const requiredAll = typeof (required) === 'string' ? [required] : (required ?? []);
  const auth = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState(null);

  const missingRequired = requiredAll.some((p) => !auth.permissions.includes(p));
  const missingAnyOf = Boolean(anyOf?.length) && !anyOf!.some((p) => auth.permissions.includes(p));

  // No criteria at all is a MISTAKE, not permission: `required` became optional when `anyOf` was added,
  // so a call site that passes neither (or an empty array — which used to throw in the old reduce) would
  // otherwise render a permission-gated page to any authenticated session.
  const hasCriteria = requiredAll.length > 0 || Boolean(anyOf?.length);
  const forbidden = !hasCriteria || missingRequired || missingAnyOf;
  if (forbidden) {
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }

    if (fallbackRoute) {
      return <Navigate to={fallbackRoute} />;
    }

    return <InsufficientPermisionPage />;
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

PermissionGuard.propTypes = {
  children: PropTypes.node
};

export default PermissionGuard;
